import { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { SettingsPanel, PhysicsSettings, BoundaryMode, XBoundaryMode } from './SettingsPanel'

interface Icon {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  image: HTMLImageElement
  dying?: boolean
  deathTimer?: number
  rotation?: number
}

export function BouncingPuzzleIcons() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const iconsRef = useRef<Icon[]>([])
  const animationFrameRef = useRef<number>()
  const loadedImagesRef = useRef<HTMLImageElement[]>([])
  const nextIconIdRef = useRef(30)
  const spawnIntervalRef = useRef<number>()

  const [settings, setSettings] = useState<PhysicsSettings>({
    friction: 0.99,
    maxIcons: 120,
    currentIcons: 0,
    spawnPerSecond: 1,
    minSpeed: 0.3,
    maxSpeed: 15,
    bounceStrength: 0.8,
    mouseRadius: 100,
    mousePushForce: 2,
    mouseMomentumTransfer: 0.8,
    boundaryMode: 'ceiling' as BoundaryMode,
    gravity: 0,
    xBoundaryMode: 'bounce' as XBoundaryMode,
  })

  const mouseRef = useRef({ x: 0, y: 0, prevX: 0, prevY: 0, vx: 0, vy: 0 })
  const settingsRef = useRef(settings)

  const handleSettingsChange = (newSettings: Partial<PhysicsSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }))
  }

  const handleReset = () => {
    // Mark excess icons for death
    const maxIcons = 120 // Default max icons
    const icons = iconsRef.current

    if (icons.length > maxIcons) {
      // Mark oldest icons (beyond the limit) for death
      const excessCount = icons.length - maxIcons
      for (let i = 0; i < excessCount; i++) {
        const icon = icons[i]
        if (!icon.dying) {
          icon.dying = true
          icon.deathTimer = 60
          icon.rotation = 0
        }
      }
    }
  }

  // Update settings ref when settings change
  useEffect(() => {
    settingsRef.current = settings
  }, [settings])

  // Update spawn interval when spawn rate changes
  useEffect(() => {
    if (spawnIntervalRef.current) {
      clearInterval(spawnIntervalRef.current)
    }

    const canvas = canvasRef.current
    if (!canvas || loadedImagesRef.current.length === 0) return

    const spawnIcon = () => {
      if (!canvas || loadedImagesRef.current.length === 0) return

      const currentSettings = settingsRef.current
      if (iconsRef.current.length >= currentSettings.maxIcons) {
        const oldestIcon = iconsRef.current[0]
        if (!oldestIcon.dying) {
          oldestIcon.dying = true
          oldestIcon.deathTimer = 60
          oldestIcon.rotation = 0
        }
      }

      const minSpeed = currentSettings.minSpeed
      const radius = 25 + Math.random() * 20
      const randomX = Math.random() * (canvas.width - radius * 2) + radius
      const randomImage = loadedImagesRef.current[Math.floor(Math.random() * loadedImagesRef.current.length)]
      const angle = Math.random() * Math.PI * 2

      const newIcon: Icon = {
        id: nextIconIdRef.current++,
        x: randomX,
        y: -radius - 100,
        vx: Math.cos(angle) * minSpeed,
        vy: Math.sin(angle) * minSpeed,
        radius,
        image: randomImage,
      }

      iconsRef.current.push(newIcon)
    }

    if (settings.spawnPerSecond > 0) {
      spawnIntervalRef.current = setInterval(() => spawnIcon(), 1000 / settings.spawnPerSecond) as unknown as number
    }

    return () => {
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current)
      }
    }
  }, [settings.spawnPerSecond, settings.maxIcons, settings.minSpeed])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Load images
    const totalImages = 100
    const initialIconCount = 30
    const imageIndices = Array.from({ length: initialIconCount }, () =>
      Math.floor(Math.random() * totalImages) + 1
    )

    const loadImage = (index: number): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = `/img/games/${index}.png`
      })
    }

    // Initialize icons
    const initIcons = async () => {
      const loadedImages = await Promise.all(
        imageIndices.map(index => loadImage(index))
      )

      loadedImagesRef.current = loadedImages

      const minSpeed = 0.3 // Minimum speed all icons maintain
      const icons: Icon[] = []

      for (let i = 0; i < loadedImages.length; i++) {
        const radius = 25 + Math.random() * 20
        const angle = Math.random() * Math.PI * 2

        icons.push({
          id: i,
          x: Math.random() * (canvas.width - radius * 2) + radius,
          y: Math.random() * (canvas.height + 200) - 100, // Include offscreen area
          vx: Math.cos(angle) * minSpeed,
          vy: Math.sin(angle) * minSpeed,
          radius,
          image: loadedImages[i],
        })
      }

      iconsRef.current = icons
      animate()
    }

    // Mouse tracking with velocity
    const handleMouseMove = (e: MouseEvent) => {
      const mouse = mouseRef.current
      mouse.prevX = mouse.x
      mouse.prevY = mouse.y
      mouse.x = e.clientX
      mouse.y = e.clientY
      mouse.vx = mouse.x - mouse.prevX
      mouse.vy = mouse.y - mouse.prevY
    }
    window.addEventListener('mousemove', handleMouseMove)

    // Spawn new icon
    const spawnIcon = () => {
      if (!canvas || loadedImagesRef.current.length === 0) return

      const currentSettings = settingsRef.current
      if (iconsRef.current.length >= currentSettings.maxIcons) {
        // Mark oldest icon for death instead of immediate removal
        const oldestIcon = iconsRef.current[0]
        if (!oldestIcon.dying) {
          oldestIcon.dying = true
          oldestIcon.deathTimer = 60 // 60 frames (~1 second at 60fps)
          oldestIcon.rotation = 0
        }
      }

      const minSpeed = currentSettings.minSpeed
      const radius = 25 + Math.random() * 20
      const randomX = Math.random() * (canvas.width - radius * 2) + radius
      const randomImage = loadedImagesRef.current[Math.floor(Math.random() * loadedImagesRef.current.length)]
      const angle = Math.random() * Math.PI * 2

      const newIcon: Icon = {
        id: nextIconIdRef.current++,
        x: randomX,
        y: -radius - 100,
        vx: Math.cos(angle) * minSpeed,
        vy: Math.sin(angle) * minSpeed,
        radius,
        image: randomImage,
      }

      iconsRef.current.push(newIcon)
    }

    // Click to spawn
    const handleClick = () => spawnIcon()
    window.addEventListener('click', handleClick)

    // Collision detection
    const checkCollision = (icon1: Icon, icon2: Icon) => {
      const dx = icon2.x - icon1.x
      const dy = icon2.y - icon1.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      return distance < icon1.radius + icon2.radius
    }

    // Resolve collision
    const resolveCollision = (icon1: Icon, icon2: Icon) => {
      const dx = icon2.x - icon1.x
      const dy = icon2.y - icon1.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance === 0) return

      const nx = dx / distance
      const ny = dy / distance

      const dvx = icon2.vx - icon1.vx
      const dvy = icon2.vy - icon1.vy
      const dvn = dvx * nx + dvy * ny

      if (dvn > 0) return

      // Bounce with energy loss from settings
      const bounceStrength = settingsRef.current.bounceStrength
      const impulse = (2 * dvn) / 2 * bounceStrength

      icon1.vx += impulse * nx
      icon1.vy += impulse * ny
      icon2.vx -= impulse * nx
      icon2.vy -= impulse * ny

      // Separate overlapping icons
      const overlap = (icon1.radius + icon2.radius - distance) / 2
      icon1.x -= overlap * nx
      icon1.y -= overlap * ny
      icon2.x += overlap * nx
      icon2.y += overlap * ny
    }

    // Animation loop
    const animate = () => {
      if (!ctx || !canvas) return

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const icons = iconsRef.current
      const mouse = mouseRef.current
      const currentSettings = settingsRef.current
      const minSpeed = currentSettings.minSpeed
      const maxSpeed = currentSettings.maxSpeed

      // Update current icon count
      setSettings((prev) => ({ ...prev, currentIcons: icons.filter(i => !i.dying).length }))

      // Remove fully dead icons
      iconsRef.current = icons.filter(icon => {
        if (icon.dying && icon.deathTimer !== undefined && icon.deathTimer <= 0) {
          return false
        }
        return true
      })

      icons.forEach((icon) => {
        // Handle dying animation
        if (icon.dying && icon.deathTimer !== undefined) {
          icon.deathTimer--
          if (icon.rotation !== undefined) {
            icon.rotation += 0.1 // Rotate faster as it dies
          }
          // Don't apply physics to dying icons, just let them fade
        }

        // Mouse interaction - massive boulder
        const dx = icon.x - mouse.x
        const dy = icon.y - mouse.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance < currentSettings.mouseRadius && !icon.dying) {
          const mouseSpeed = Math.sqrt(mouse.vx * mouse.vx + mouse.vy * mouse.vy)

          // Radial push force
          const force = ((currentSettings.mouseRadius - distance) / currentSettings.mouseRadius) * currentSettings.mousePushForce
          const angle = Math.atan2(dy, dx)
          icon.vx += Math.cos(angle) * force
          icon.vy += Math.sin(angle) * force

          // Massive momentum transfer in mouse direction
          if (mouseSpeed > 0.1) {
            const mouseDirX = mouse.vx / mouseSpeed
            const mouseDirY = mouse.vy / mouseSpeed
            icon.vx += mouseDirX * mouseSpeed * currentSettings.mouseMomentumTransfer
            icon.vy += mouseDirY * mouseSpeed * currentSettings.mouseMomentumTransfer
          }
        }

        // Only apply physics to living icons
        if (!icon.dying) {
          // Apply gravity
          icon.vy += currentSettings.gravity

          // Apply friction from settings
          icon.vx *= currentSettings.friction
          icon.vy *= currentSettings.friction

          // Apply velocity
          icon.x += icon.vx
          icon.y += icon.vy

          // X walls - wrap or bounce depending on setting
          if (currentSettings.xBoundaryMode === 'wrap') {
            // Wrap mode - teleport to opposite side
            if (icon.x - icon.radius > canvas.width + 100) {
              icon.x = -icon.radius - 100
            } else if (icon.x + icon.radius < -100) {
              icon.x = canvas.width + icon.radius + 100
            }
          } else {
            // Bounce mode - bounce with energy loss
            if (icon.x - icon.radius < 0) {
              icon.x = icon.radius
              icon.vx *= -currentSettings.bounceStrength
            } else if (icon.x + icon.radius > canvas.width) {
              icon.x = canvas.width - icon.radius
              icon.vx *= -currentSettings.bounceStrength
            }
          }

          // Y boundaries - depend on mode
          if (currentSettings.boundaryMode === 'ceiling') {
            // Teleport mode (original behavior)
            if (icon.y - icon.radius > canvas.height + 100) {
              icon.y = -icon.radius - 100
            } else if (icon.y + icon.radius < -100) {
              icon.y = canvas.height + icon.radius + 100
            }
          } else {
            // Boxed mode - bounce on Y walls
            if (icon.y - icon.radius < 0) {
              icon.y = icon.radius
              icon.vy *= -currentSettings.bounceStrength
            } else if (icon.y + icon.radius > canvas.height) {
              icon.y = canvas.height - icon.radius
              icon.vy *= -currentSettings.bounceStrength
            }
          }

          // Hard speed cap
          if (Math.abs(icon.vx) > maxSpeed) {
            icon.vx = icon.vx > 0 ? maxSpeed : -maxSpeed
          }
          if (Math.abs(icon.vy) > maxSpeed) {
            icon.vy = icon.vy > 0 ? maxSpeed : -maxSpeed
          }

          // Maintain minimum speed
          const currentSpeed = Math.sqrt(icon.vx * icon.vx + icon.vy * icon.vy)
          if (currentSpeed > 0 && currentSpeed < minSpeed) {
            const scale = minSpeed / currentSpeed
            icon.vx *= scale
            icon.vy *= scale
          }
        }

        // Draw icon with death animation
        ctx.save()

        if (icon.dying && icon.deathTimer !== undefined) {
          // Calculate death progress (0 = just started dying, 1 = fully dead)
          const deathProgress = 1 - (icon.deathTimer / 60)

          // Fade out
          ctx.globalAlpha = 0.9 * (1 - deathProgress)

          // Shrink
          const shrinkFactor = 1 - (deathProgress * 0.7) // Shrink to 30% of original size
          const currentRadius = icon.radius * shrinkFactor

          // Rotate
          ctx.translate(icon.x, icon.y)
          ctx.rotate(icon.rotation || 0)
          ctx.drawImage(
            icon.image,
            -currentRadius,
            -currentRadius,
            currentRadius * 2,
            currentRadius * 2
          )
        } else {
          ctx.globalAlpha = 0.9
          ctx.drawImage(
            icon.image,
            icon.x - icon.radius,
            icon.y - icon.radius,
            icon.radius * 2,
            icon.radius * 2
          )
        }

        ctx.restore()
      })

      // Check collisions
      for (let i = 0; i < icons.length; i++) {
        for (let j = i + 1; j < icons.length; j++) {
          if (checkCollision(icons[i], icons[j])) {
            resolveCollision(icons[i], icons[j])
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    initIcons()

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('click', handleClick)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
          }}
        />
      </Box>
      <SettingsPanel settings={settings} onSettingsChange={handleSettingsChange} onReset={handleReset} />
    </>
  )
}
